import {
  Button,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Text,
  Title1,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeft24Regular, Settings24Regular } from '@fluentui/react-icons';

import { DebugDetailView } from '../../components/debug/DebugDetailView/DebugDetailView.js';
import { DebugTrajectoryTimeline } from '../../components/debug/DebugTrajectoryTimeline/DebugTrajectoryTimeline.js';
import { InteractionList } from '../../components/debug/InteractionList/InteractionList.js';
import { OverviewView } from '../../components/debug/OverviewView/OverviewView.js';
import { config } from '../../config.js';
import { useAgentDebugData } from '../../hooks/useAgentDebugData/useAgentDebugData.js';
import { useAuth } from '../../hooks/useAuth/useAuth.js';

import { useStyles } from './AgentDebugPage.style';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AgentDebugPage() {
  const styles = useStyles();
  const { account, getToken } = useAuth();
  const safeGetToken = config.apiAuthEnabled
    ? getToken
    : () => Promise.resolve('');
  const {
    tokenInput,
    setTokenInput,
    handleTokenBlur,
    pageView,
    cvInput,
    setCvInput,
    fetchLoading,
    fetchError,
    eligibilityRequired,
    parsedData,
    detailViewState,
    navOrigin,
    selectedData,
    activeOverviewTab,
    setActiveOverviewTab,
    overviewEntries,
    overviewLoading,
    overviewError,
    fetchOverview: _fetchOverview,
    sortedOverviewEntries,
    recentSortBy,
    recentSortDir,
    handleRecentSort,
    timelineEntries,
    timelineLoading,
    timelineError,
    fetchTimeline,
    calendarFilterAgents,
    setCalendarFilterAgents,
    calendarAgentNames,
    calendarFilterPlatforms,
    setCalendarFilterPlatforms,
    calendarPlatformNames,
    filteredTimelineEntries,
    timelineDays,
    loadJsonInput,
    setLoadJsonInput,
    loadJsonError,
    handleLoadJson,
    loadJsonMode,
    setLoadJsonMode,
    loadedTrajectory,
    trajectoryLoading,
    trajectoryError,
    handleTrajectoryBack,
    handleSelect,
    handleBack,
    handleOverviewSelect,
    shareDescription,
  } = useAgentDebugData(safeGetToken, account?.localAccountId);

  return (
    <div className={styles.root}>
      {/* Page header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderInner}>
          <Title1>Debug</Title1>

          <div className={styles.pageHeaderSpacer} />

          <Popover positioning="below-end">
            <PopoverTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                icon={<Settings24Regular />}
                title="Settings"
              />
            </PopoverTrigger>
            <PopoverSurface>
              <div className={styles.settingsPanel}>
                <span className={styles.settingsLabel}>
                  Substrate Bearer Token
                </span>
                <Input
                  placeholder="Paste token — only needed when OBO is not available"
                  value={tokenInput}
                  onChange={(_, d) => setTokenInput(d.value)}
                  onBlur={handleTokenBlur}
                  type="password"
                  appearance="outline"
                />
                <Text className={styles.settingsHint}>
                  Saved automatically when you click away. When set, requests
                  bypass OBO and use this token directly.
                </Text>
                {tokenInput && (
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => {
                      setTokenInput('');
                    }}
                  >
                    Clear Token
                  </Button>
                )}
              </div>
            </PopoverSurface>
          </Popover>
        </div>
      </div>

      {/* Main content */}
      <div className={styles.content}>
        <div className={styles.contentInner}>
          {/* ---- OVERVIEW ---- */}
          {pageView === 'overview' && (
            <OverviewView
              activeTab={activeOverviewTab}
              onTabChange={setActiveOverviewTab}
              overviewEntries={overviewEntries}
              overviewLoading={overviewLoading}
              overviewError={overviewError}
              sortedOverviewEntries={sortedOverviewEntries}
              recentSortBy={recentSortBy}
              recentSortDir={recentSortDir}
              onRecentSort={handleRecentSort}
              timelineEntries={timelineEntries}
              timelineLoading={timelineLoading}
              timelineError={timelineError}
              calendarFilterAgents={calendarFilterAgents}
              calendarAgentNames={calendarAgentNames}
              calendarFilterPlatforms={calendarFilterPlatforms}
              calendarPlatformNames={calendarPlatformNames}
              filteredTimelineEntries={filteredTimelineEntries}
              timelineDays={timelineDays}
              onCalendarFilterChange={setCalendarFilterAgents}
              onCalendarPlatformFilterChange={setCalendarFilterPlatforms}
              onRefreshTimeline={() => void fetchTimeline(timelineDays, true)}
              cvInput={cvInput}
              onCvInputChange={setCvInput}
              fetchLoading={fetchLoading}
              loadJsonInput={loadJsonInput}
              loadJsonError={loadJsonError}
              onLoadJsonChange={setLoadJsonInput}
              onLoadJson={handleLoadJson}
              loadJsonMode={loadJsonMode}
              onLoadJsonModeChange={setLoadJsonMode}
              onSelect={handleOverviewSelect}
            />
          )}

          {/* ---- STANDALONE TRAJECTORY ---- */}
          {pageView === 'trajectory' && (
            <div className={styles.trajectoryView}>
              <div>
                <Button
                  appearance="subtle"
                  icon={<ArrowLeft24Regular />}
                  onClick={handleTrajectoryBack}
                >
                  Back to Load from JSON
                </Button>
              </div>
              {trajectoryError && (
                <MessageBar intent="error">
                  <MessageBarBody style={{ whiteSpace: 'pre-wrap' }}>
                    Could not load the trajectory from this link:{' '}
                    {trajectoryError}
                  </MessageBarBody>
                </MessageBar>
              )}
              {trajectoryLoading ? (
                <div className={styles.emptyState}>
                  <Spinner size="large" label="Loading trajectory..." />
                </div>
              ) : loadedTrajectory ? (
                <DebugTrajectoryTimeline payload={loadedTrajectory} />
              ) : trajectoryError ? null : (
                <div className={styles.emptyState}>
                  <p>
                    No trajectory loaded. Use the{' '}
                    <strong>Load from JSON</strong> tab to paste an
                    ExecutionTrajectory.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ---- DETAIL ---- */}
          {pageView === 'detail' && (
            <>
              {eligibilityRequired && (
                <MessageBar
                  intent="warning"
                  style={{ marginBottom: tokens.spacingVerticalM }}
                >
                  <MessageBarBody>
                    You do not have access to view shared signals. Please{' '}
                    <Link
                      href="https://m365pulse.microsoft.com/CorpIdentity/RequestAccess/RequestScenario?scenarioName=AIStudio-View-Shared-Signals"
                      target="_blank"
                    >
                      request the eligibility
                    </Link>{' '}
                    to view signals shared by other users. Signout and back in
                    afterwards.
                  </MessageBarBody>
                </MessageBar>
              )}

              {fetchError && (
                <MessageBar
                  intent="error"
                  style={{ marginBottom: tokens.spacingVerticalM }}
                >
                  <MessageBarBody style={{ whiteSpace: 'pre-wrap' }}>
                    {fetchError}
                  </MessageBarBody>
                </MessageBar>
              )}

              {fetchLoading && (
                <div className={styles.emptyState}>
                  <Spinner
                    size="large"
                    label={`Fetching signal for CV: ${cvInput}...`}
                  />
                </div>
              )}

              {!fetchLoading &&
                !eligibilityRequired &&
                detailViewState === 'input' &&
                parsedData.length === 0 &&
                !fetchError && (
                  <div className={styles.emptyState}>
                    <p>
                      Use the <strong>Search for Interaction</strong> tab to
                      load a signal by CV or ItemId.
                    </p>
                  </div>
                )}

              {!fetchLoading && detailViewState === 'empty' && (
                <div className={styles.emptyState}>
                  <p>No signals found for this CV.</p>
                </div>
              )}

              {!fetchLoading &&
                detailViewState === 'list' &&
                parsedData.length > 0 && (
                  <>
                    <div className={styles.listHeader}>
                      <span className={styles.listCount}>
                        {parsedData.length} signal
                        {parsedData.length !== 1 ? 's' : ''} found
                      </span>
                    </div>
                    <InteractionList
                      data={parsedData}
                      onSelect={handleSelect}
                    />
                  </>
                )}

              {!fetchLoading &&
                detailViewState === 'detail' &&
                selectedData && (
                  <DebugDetailView
                    data={selectedData}
                    origin={navOrigin}
                    onBack={handleBack}
                    onSelectCv={handleOverviewSelect}
                    shareDescription={shareDescription}
                  />
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
